import { BaseComponent2 } from '../../_common/client/base-component2';
import { CustomerReviewLightbox } from '../../_common/client/customer-review-lightbox';
import { OcCarouselV1Events } from '@otto-ec/otto-components/carousel';
import { trackEvent } from '@otto-ec/nexus-registry/pdp/tracking';
import { sheetV1 } from '@otto-ec/otto-components/sheet';
import { isActiveBreakpoint } from '@otto-ec/global-resources/viewport';
import { ensureCarouselFillsHeight } from './helper/ensure-carousel-fills-height';
import { SheetReviewExpanderResetter } from './service/sheet-review-expander-resetter';

const SELECTOR_IMAGE_CARD = '.js_pdp_cr-image-gallery__image';
const SELECTOR_IMAGES = '.js_pdp_cr-image-gallery__images';
const SELECTOR_SHEET_TEMPLATE = '.js_pdp_cr-image-gallery__sheet-template';
const SELECTOR_SHEET_CAROUSEL = '.js_pdp_cr-image-gallery__sheet-carousel';
const SELECTOR_SHEET_SLIDE = '.js_pdp_cr-image-gallery__sheet-slide';
const SELECTOR_SHEET_REVIEW = '.js_pdp_cr-image-gallery__sheet-review';
const SELECTOR_SHEET_REVIEW_ITEM = '.js_pdp_cr-image-gallery__sheet-review-item';

export class CustomerReviewImageGalleryComponent extends BaseComponent2 {
    public rootSelector = '.js_pdp_cr-image-gallery';

    private readonly reviewExpanderResetter = new SheetReviewExpanderResetter();

    private sheetElement?: HTMLOcSheetV1Element;
    private carouselElement?: HTMLOcCarouselV1Element;
    private isFirstSwipeChange = true;

    public async init(): Promise<void> {
        if (!this.isPresent()) return;

        const sheetTemplateElement = this.getSheetTemplateElement();

        if (sheetTemplateElement) {
            await this.initSheet(sheetTemplateElement);
        } else {
            await CustomerReviewLightbox.init(this.getRootElement(), SELECTOR_IMAGES, SELECTOR_IMAGE_CARD, 'top');
        }
    }

    private async initSheet(sheetTemplateElement: HTMLTemplateElement): Promise<void> {
        const sheetContentFragment = sheetTemplateElement.content.cloneNode(true) as DocumentFragment;
        const carouselElement = sheetContentFragment.querySelector<HTMLOcCarouselV1Element>(SELECTOR_SHEET_CAROUSEL);
        if (!carouselElement) return;

        const reviewElement = sheetContentFragment.querySelector<HTMLElement>(SELECTOR_SHEET_REVIEW);
        if (reviewElement) this.preventSheetDragWhileScrollingReview(reviewElement);

        const sheetElement = await sheetV1.create({
            id: 'pdp_cr-image-gallery__sheet',
            adaptiveAlignment: 'center',
            hideHeader: true,
            noContentPadding: true,
            fullHeight: true,
        });
        sheetElement.classList.add('pdp_cr-image-gallery__sheet');
        sheetElement.replaceChildren(sheetContentFragment);

        this.sheetElement = sheetElement;
        this.carouselElement = carouselElement;

        this.updateCarouselControlsVisibility(carouselElement);
        ensureCarouselFillsHeight(carouselElement);

        const imageCardElements = this.getImageCardElements();
        const imageCardElementsLength = imageCardElements.length;

        imageCardElements.forEach(cardElement => {
            cardElement.addEventListener('click', () => this.openSheet(cardElement, imageCardElementsLength));
        });

        carouselElement.addEventListener('oc-item-visibility-changed', event =>
            this.handleItemVisibilityChanged(carouselElement, event, imageCardElementsLength)
        );
        sheetElement.addEventListener('oc-sheet-close', () => {
            this.reviewExpanderResetter.reset(this.getReviewElements());
            this.isFirstSwipeChange = true;
        });
    }

    private preventSheetDragWhileScrollingReview(reviewElement: HTMLElement): void {
        reviewElement.addEventListener('touchmove', event => event.stopPropagation(), { passive: true });
    }

    private openSheet(cardElement: HTMLElement, imageCardElementsLength: number): void {
        const sheetElement = this.sheetElement;
        const carouselElement = this.carouselElement;
        if (!sheetElement || !carouselElement) return;

        const index = Number(cardElement.dataset.imageIndex ?? 0);

        /*                                                                                        */
        /*                                                                                         */
        /*                                                          */
        sheetElement.addEventListener(
            'oc-sheet-open',
            () => {
                carouselElement.moveTo(index, { reducedMotion: true });
                this.showReviewForSlideIndex(carouselElement, index);
            },
            { once: true }
        );

        sheetElement.open = true;

        this.trackSheetOpened(cardElement, index, imageCardElementsLength);
    }

    private handleItemVisibilityChanged(
        carouselElement: HTMLOcCarouselV1Element,
        event: OcCarouselV1Events['oc-item-visibility-changed'],
        numImageItems: number
    ): void {
        if (event.detail.stageIsScrolling) {
            this.showReviewForSlideIndex(carouselElement, event.detail.visibleItem.index);
            return;
        }

        if (event.detail.scrollSource === 'swipe' || event.detail.scrollSource === 'arrows') {
            this.trackSwipe(event.detail.visibleItem.index, numImageItems);
        }
    }

    /**
 *
 *
 *
 */
    private showReviewForSlideIndex(carouselElement: HTMLOcCarouselV1Element, index: number): void {
        const slideElement = this.getSlideElements(carouselElement)[index];
        const reviewId = slideElement?.dataset.reviewId;
        if (!reviewId) return;

        const reviewElements = this.getReviewElements();
        this.reviewExpanderResetter.handleReviewShown(reviewId, reviewElements);

        reviewElements.forEach(reviewElement => {
            const shouldBeVisible = reviewElement.dataset.reviewId === reviewId;
            if (reviewElement.hidden === !shouldBeVisible) return;
            reviewElement.hidden = !shouldBeVisible;
        });
    }

    private trackSheetOpened(cardElement: HTMLElement, index: number, numImageItems: number): void {
        const rating = cardElement.dataset.rating ?? '0';

        trackEvent({
            product_ValenceOfOpenedReviewImage: [rating],
            product_DifferentReviewImageChange: [`${index + 1}|${numImageItems}`],
            product_ReviewImageGallery: ['full_gallery'],
            product_ReviewImageView: ['top'],
        });
    }

    private trackSwipe(index: number, numImageItems: number): void {
        trackEvent({
            product_ReviewImageView: [this.isFirstSwipeChange ? 'change_first' : 'change_following'],
            product_ReviewImageGallery: ['full_gallery'],
            product_DifferentReviewImageChange: [`${index + 1}|${numImageItems}`],
        });
        this.isFirstSwipeChange = false;
    }

    private async updateCarouselControlsVisibility(carouselElement: HTMLOcCarouselV1Element): Promise<void> {
        const isMobileDevice: boolean = await isActiveBreakpoint(['s']);
        carouselElement.hideArrowButtons = isMobileDevice;
        carouselElement.hidePaginationDots = !isMobileDevice;
    }

    private getSheetTemplateElement(): HTMLTemplateElement | undefined {
        return this.getChildElement<HTMLTemplateElement>(SELECTOR_SHEET_TEMPLATE);
    }

    private getImageCardElements(): HTMLElement[] {
        return this.getChildElements<HTMLElement>(SELECTOR_IMAGE_CARD);
    }

    private getSlideElements(carouselElement: HTMLOcCarouselV1Element): HTMLElement[] {
        return Array.from(carouselElement.querySelectorAll<HTMLElement>(SELECTOR_SHEET_SLIDE));
    }

    private getReviewElements(): HTMLElement[] {
        /*                                                                                 */
        /*                                                                                    */
        return this.sheetElement ? Array.from(this.sheetElement.querySelectorAll<HTMLElement>(SELECTOR_SHEET_REVIEW_ITEM)) : [];
    }
}
